import React, { useEffect, useState, useRef, useMemo } from 'react';
import styles from '../Patient.module.css';
import { useSelector, useDispatch } from 'react-redux';
import PatientDetailDropdownCard from '../../../components/PatientDetailDropdownCard';
import AlarmDetails from '../CardDetails/AlarmDetails';
import CustomToggle from '../../../components/CustomToggle';
import inputStyles from '../../ManagementPages.module.css';
import MultiSelectDropdown from '../../../components/MultiSelectDropdown';
import AlarmHistoryDetails from '../CardDetails/AlarmHistoryDetails';
import { getAlertTimestamp } from '../../../utils/alertUtils';
import { exportAlarmsToCSV } from '../../../utils/csvExportUtils';
import useAxios from '../../../services/AxiosConfig';
import { setAlerts } from '../../../slicers/PatientSlice';

const AlarmsView = () => {

    const { alerts, alertTypeOpen, taskAlertId} = useSelector((state) => state.patient);
    const careTeam = useSelector((state) => state.patient.careTeam);
    const patient = useSelector((state) => state.patient);
    const patientId = patient.id;
    const [alertsTypeToggle, setAlertsTypeToggle] = useState('active');
    const [historySort, setHistorySort] = useState('descending');
    const [selectedAlertTypes, setSelectedAlertTypes] = useState([]);
    const [searchText, setSearchText] = useState('');
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchAlerts = async () => {
            const alertResponse = await axios.get(`alerts/${patientId}`);
            if (Array.isArray(alertResponse.data.data)) {
                dispatch(setAlerts(alertResponse.data.data));
            }
        }
        fetchAlerts();
    }, []);

    const alertTypes = [
        { value: "clinical", label: "Clinical" },
        { value: "technical", label: "Technical" },
        { value: "operational", label: "Operational" }
    ];

    const checkSelectedItem = (value) => {
        return selectedAlertTypes.some(item => item.value === value);
    };

    const handleAlertSelection = (item) => {
        const isSelected = checkSelectedItem(item.value);
        const updatedFilterTypes = isSelected
            ? selectedAlertTypes.filter(selectedItem => selectedItem?.value?.toLowerCase() !== item?.value?.toLowerCase())
            : [...selectedAlertTypes, item];

        setSelectedAlertTypes(updatedFilterTypes);

    };

    const handleToggle = (value) => {
        setAlertsTypeToggle(value)
    };

    const sortedHistory = useMemo(() => {
        if (alertsTypeToggle !== 'history') return [];
        return [...alerts.history]
            .filter((alert) => {
                const matchesAlertType =
                    selectedAlertTypes.length === 0 ||
                    selectedAlertTypes.some(type => type.value === alert.alert_category);

                const matchesSearchText = searchText === "" || alert.alert_description?.toLowerCase().includes(searchText.toLowerCase());
                return matchesSearchText && matchesAlertType;
            })
            .sort((a, b) => {
                const timestampA = getAlertTimestamp(a);
                const timestampB = getAlertTimestamp(b);
                const startDateA = new Date(timestampA);
                const startDateB = new Date(timestampB);
                return historySort === "ascending" ? startDateA - startDateB : startDateB - startDateA;
            });
    }, [alerts.history, selectedAlertTypes, searchText, historySort, alertsTypeToggle]);

    const activeAlarms = useMemo(() => {
        if (alertsTypeToggle !== 'active') return [];
        return alerts.active.all || [];
    }, [alerts.active.all, alertsTypeToggle]);

    const alarmsToExport = useMemo(() => {
        return alertsTypeToggle === 'active' ? activeAlarms : sortedHistory;
    }, [alertsTypeToggle, activeAlarms, sortedHistory]);

    const handleExportCSV = () => {
        exportAlarmsToCSV(alarmsToExport, careTeam, patientId);
    };

    const alertRefs = useRef([]);

    useEffect(() => {
        if ((alertTypeOpen || taskAlertId) && alertRefs.current) {
            let alertType = '';
            if (alertTypeOpen) {
                alertType = alertTypeOpen;
            } else if (taskAlertId !== '') {
                const taskAlert = alerts.active.all.find((item)=>item.alertId === taskAlertId);
                if (taskAlert) {
                    alertType = taskAlert.alert_category;
                }
            }
            const element = alertRefs.current[alertType];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [alertTypeOpen, alerts, taskAlertId]);

    return (
        <div className={styles.ViewContainer}>
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingLeft: "5px",
                paddingRight: "10px",
                flexWrap: "nowrap",
                width: "100%"
            }}>
                <CustomToggle labels={[{ value: "active", name: "Active" }, { value: "history", name: "History" }]} selectedToggle={alertsTypeToggle} handleToggle={handleToggle} />
                <div style={{ display: "flex", columnGap: "10px" }}>
                    {alertsTypeToggle === 'history' &&
                        <>
                        <div style={{ position: "relative" }}>
                            <span className="floating-label">Date</span>
                            <select
                                id="dateOrder"
                                style={{ width: "110px" }}
                                className={inputStyles["styled-input-select"]}
                                onChange={(e) => setHistorySort(e.target.value)}
                                value={historySort}
                            >
                                <option key={"descending"} value={"descending"}>
                                    Descending
                                </option>
                                <option key={"ascending"} value={"ascending"}>
                                    Ascending
                                </option>
                            </select>
                        </div>

                        <MultiSelectDropdown
                            inputWidth='200px'
                            options={alertTypes}
                            label='label'
                            checkedKey='value'
                            isSearch={false}
                            checkSelectedItem={checkSelectedItem}
                            handleChange={handleAlertSelection}
                            selectedItems={selectedAlertTypes}
                            name='Alarm Types'
                        />


                        <div className={inputStyles["styled-input-container"]}>
                            <input
                                id="searchText"
                                type="text"
                                style={{ width: "200px" }}
                                className={inputStyles["styled-input-container"]}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <label className={searchText && inputStyles['filled']}>
                                Search...
                            </label>
                        </div>
                        </>
                    }
                    <button
                        onClick={handleExportCSV}
                        disabled={alarmsToExport.length === 0}
                        className={styles.SecondaryButton}
                    >
                        Export
                    </button>
                </div>
            </div>
            <div className={styles.PatientViewScrollContainer}>
                {alertsTypeToggle === 'active' &&
                    alertTypes.map((item)=> (
                        <PatientDetailDropdownCard
                            key={item.value}
                            title={`${item.label} Alarms`}
                            subtitle2={`${alerts.active[item.value].length} alarm${alerts.active[item.value].length !== 1 ? "s" : ""}`}
                            cardInfo={<AlarmDetails type={item.value} />}
                            isOpen={alertTypeOpen === item.value || alerts.active[item.value].find((item)=>item.alertId === taskAlertId)}
                            ref={el => alertRefs.current[item.value] = el}
                            width={'1165px'}
                        />
                    ))
                }
                {alertsTypeToggle === 'history' &&
                    <AlarmHistoryDetails 
                        historySort={historySort}
                        selectedAlertTypes={selectedAlertTypes}
                        searchText={searchText}
                        alertsHistory={alerts.history}
                    />
                }
            </div>
        </div>
    );
}

export default AlarmsView;